/**
 * Druck-System (Welle 4): druckfertiges HTML von der API holen und in einem neuen Tab oeffnen
 * (Blob-URL statt document.write - funktioniert mit dem Auth-Header von apiFetch, den ein
 * simples `<a href>` nicht mitschicken koennte).
 */
import { apiFetch, toApiError } from "./api";

/**
 * Tab SYNCHRON im Klick oeffnen, Inhalt danach nachladen (Antoine, 16.10.2026, iPhone/Safari:
 * „Vorschau funktioniert nicht"). Safari erlaubt `window.open` nur, solange es direkt in der
 * Klick-Geste laeuft — nach `await apiFetch(...)` ist die Geste verbraucht und der Tab wird
 * stillschweigend blockiert (`window.open` liefert `null`, kein Fehler, nichts passiert).
 * Darum: erst leeren Tab oeffnen, dann laden, dann den Tab auf die Blob-URL lenken.
 * Aufrufer muessen diese Funktion aus dem Klick heraus OHNE vorheriges `await` erreichen.
 */
export async function oeffneImNeuenTab(lade: () => Promise<Blob>): Promise<void> {
  const win = window.open("", "_blank");
  let blob: Blob;
  try {
    blob = await lade();
  } catch (e) {
    win?.close();
    throw e;
  }
  const url = URL.createObjectURL(blob);
  if (!win) {
    URL.revokeObjectURL(url); // trotzdem blockiert -> keine haengende Object-URL
    return;
  }
  win.location.href = url;
  // ponytail: feste Frist statt load-Event — ein Listener haette am about:blank-Dokument gehangen,
  // nicht am Ziel. Eine Minute reicht zum Laden; das geladene Dokument bleibt im Tab erhalten.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * `charset=utf-8` ist hier NICHT optional (Antoine, 03.08.2026: "Umlaute stimmen nicht" —
 * in der E-Mail-Vorschau stand "GetrÃ¤nke", "fÃ¼r", "GrÃ¼ÃŸen").
 *
 * Die Kette: Die API liefert korrektes UTF-8 (Hono setzt bei `c.html()` den charset im
 * Content-Type), `res.text()` dekodiert danach richtig — bis hierhin ist alles heil. Erst
 * dieser Blob wirft die Information weg: Ohne charset im Typ rät der Browser beim Öffnen der
 * Blob-URL, und rät bei deutschem Text gern Windows-1252. Jedes Mehrbyte-Zeichen zerfällt
 * dann in seine Einzelbytes.
 *
 * Nicht betroffen ist lib/files.ts — dort entsteht der Blob über `res.blob()` und erbt den
 * Content-Type der Antwort samt charset. Der Fehler steckt genau im Umweg über Text.
 */
async function htmlBlob(res: Response): Promise<Blob> {
  if (!res.ok) throw await toApiError(res);
  return new Blob([await res.text()], { type: "text/html;charset=utf-8" });
}

export function openPrintableDocument(path: string): Promise<void> {
  return oeffneImNeuenTab(async () => htmlBlob(await apiFetch(path)));
}

/**
 * Wie `openPrintableDocument`, aber POST mit Body (09.09.2026, Erinnerung-Vorschau): eine
 * Mail-Vorschau MIT dem gerade im Compose-Dialog editierten Entwurf (Betreff/Anschreiben) kann
 * nicht per GET/Query gehen - Laenge und Sonderzeichen. Derselbe Blob-URL-Umweg samt
 * `charset=utf-8`-Begruendung wie oben gilt unveraendert.
 */
export function openPrintableDocumentPost(path: string, body: unknown): Promise<void> {
  return oeffneImNeuenTab(async () => htmlBlob(await apiFetch(path, { method: "POST", body: JSON.stringify(body) })));
}
